<template>
    <div class="drop-zone" :class="{ 'is-dragover': isDragOver }" @dragenter.prevent="onDragEnter"
        @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop" @click="pickFile">
        <input ref="fileInput" type="file" class="file-input" :accept="allowedTypes" :multiple="multiple"
            @change="onFileSelect" hidden />

        <!-- Empty state -->
        <div v-if="previewItems.length === 0">
            <small>
                Glissez votre document ici,<br />
                ou <span class="blue--text text-decoration-underline">parcourir</span>
            </small>
        </div>

        <!-- Preview state -->
        <div v-else class="previews">
            <v-row dense>
                <v-col v-for="(item, idx) in previewItems" :key="item.key" cols="12" sm="6" md="4" @click.stop>
                    <v-card outlined class="preview-card d-flex flex-column">
                        <v-btn icon x-small class="align-self-end" @click.stop="removeAt(idx)">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>

                        <v-img v-if="item.kind === 'image'" :src="item.url" height="120" contain />

                        <div v-else class="pdf-thumb">
                            <v-icon large>mdi-file-pdf-box</v-icon>
                            <div class="pdf-label">PDF</div>
                        </div>

                        <v-card-text class="py-2">
                            <div class="file-name" :title="item.file.name">
                                {{ item.file.name }}
                            </div>
                            <div class="file-meta">
                                {{ humanSize(item.file.size) }}
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DocumentDropZone',

    props: {
        multiple: { type: Boolean, default: false },
        maxFileSize: { type: Number, default: 0 },
        allowedTypes: { type: Array, default: () => [] }
    },

    data: () => ({
        isDragOver: false,
        dragCounter: 0,
        files: [],
        previewItems: []
    }),

    methods: {
        onDragEnter() {
            if (!this.dragCounter++) {
                this.isDragOver = true;
            }
        },

        onDragLeave(e) {
            this.dragCounter--;
            if (!this.$el.contains(e.relatedTarget)) {
                this.isDragOver = false;
                this.dragCounter = 0;
            }
        },

        onDragOver(e) {
            e.preventDefault();
        },

        pickFile() {
            this.$refs.fileInput.click();
        },

        onDrop(e) {
            this.isDragOver = false;
            this.dragCounter = 0;
            this.handleIncomingFiles(Array.from(e.dataTransfer.files));
        },

        onFileSelect(e) {
            this.handleIncomingFiles(Array.from(e.target.files));
            e.target.value = '';
        },

        handleIncomingFiles(files) {
            if (!files.length) {
                return;
            }

            const typeCheck = files.every((file) => this.isValidType(file));
            if (!typeCheck) {
                this.$notify({
                    title: 'Attention',
                    text: 'Format non autorisé',
                    type: 'warn'
                });
                return;
            }

            const sizeCheck = files.every((file) => this.isValidSize(file));
            if (!sizeCheck) {
                this.$notify({
                    title: 'Attention',
                    text: 'Fichier trop volumineux',
                    type: 'warn'
                });
                return;
            }

            this.addFiles(files);
            this.buildPreviews();
        },

        isValidType(file) {
            return this.allowedTypes.includes(file.type);
        },

        isValidSize(file) {
            if (this.maxFileSize && this.maxFileSize > 0) {
                return file.size <= this.maxFileSize;
            }

            return true;
        },

        addFiles(incoming) {
            if (this.multiple) {
                this.files = [...this.files, ...incoming];
                this.$emit('update:files', this.files);
            } else {
                this.files = [incoming[0]];
                this.$emit('update:files', this.files[0]);
            }
        },

        buildPreviews() {
            this.revokeAllObjectUrls();

            this.previewItems = (this.files || []).map(file => {
                const kind = file.type === 'application/pdf' ? 'pdf' : 'image';

                return {
                    key: `${file.name}_${file.size}_${file.lastModified}`,
                    file,
                    kind,
                    url: URL.createObjectURL(file)
                };
            });
        },

        revokeAllObjectUrls() {
            this.previewItems.forEach(item => {
                if (item.url) {
                    URL.revokeObjectURL(item.url);
                }
            });
        },

        removeAt(index) {
            const removed = this.previewItems[index];
            if (removed.url) {
                URL.revokeObjectURL(removed.url);
            }

            this.previewItems.splice(index, 1);
            this.files.splice(index, 1);

            if (this.multiple) {
                this.$emit('update:files', this.files);
            } else {
                this.$emit('update:files', this.files[0] || null);
            }
        },

        humanSize(bytes) {
            const kb = bytes / 1024;
            return kb < 1024 ? `${Math.round(kb)} Ko` : `${(kb / 1024).toFixed(1)} Mo`;
        },

        reset() {
            this.files = [];
            this.revokeAllObjectUrls();
            this.previewItems = [];
        }
    },

    beforeDestroy() {
        this.revokeAllObjectUrls();
    }
};
</script>

<style scoped>
.drop-zone {
    border: 2px dashed #aaa;
    border-radius: 6px;
    padding: 2rem;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s;
}

.drop-zone.is-dragover {
    background-color: #f0f8ff;
    border-color: #3399ff;
}

.previews {
    text-align: left;
}

.preview-card {
    position: relative;
    cursor: default;
}

.file-name {
    font-size: 0.9rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.file-meta {
    font-size: 0.8rem;
    opacity: 0.75;
}

.pdf-thumb {
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}

.pdf-label {
    margin-top: 6px;
    font-size: 0.8rem;
    opacity: 0.8;
}
</style>
